/**
 * VideoWorkspaceShell - Main container for the Video/Slide Workspace
 * Provides the overall layout structure with three-panel layout
 * Each panel has its own header row for a clean, integrated design
 *
 * Supports three modes:
 * - 'video': Multi-slide workspace with filmstrip navigation
 * - 'slide': Single-slide workspace (simplified UI, no filmstrip)
 * - 'outline': Pre-generation outline review mode
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { useVideoWorkspaceStore } from '@/stores/videoWorkspaceStore';
import { useConversationStore } from '@/stores/conversationStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useMessageStore } from '@/stores/messageStore';
import { VideoWorkspacePanels, type WorkspaceTab } from './VideoWorkspacePanels';
import { MobileVideoWorkspace } from './mobile';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useWorkspaceCdnSession } from '@/hooks/useWorkspaceCdnSession';
import { slidesAPI, outlineAPI } from '@/services/api';
import { conversationUpdatesService } from '@/services/conversationUpdatesService';
import { useMessage, type MessageContext } from '@/hooks/useMessage';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { ProviderType } from '@/services/messageService';
import { mergeServerOutlineIntoLocal } from '@/utils/outlineMerge';
import { studioBOn } from '@/lib/studioB/flag';
import type { FrontDoorStart } from '@/lib/frontDoor/handoff';

export type WorkspaceMode = 'slide' | 'video' | 'outline';

interface VideoWorkspaceShellProps {
  conversationId: string;
  mode?: WorkspaceMode;
  /** Tab to open on mount (from the ?tab= deep link) */
  initialTab?: WorkspaceTab;
  creationMode?: string;
  /** Auto-send initial message on mount (from hero page navigation) */
  autoSend?: boolean;
  /** Initial message content to send (user instructions from hero page) */
  initialMessage?: string;
  /** What a front-door start handed over in the history state (the idea and the look). */
  frontDoor?: FrontDoorStart;
  /** AI provider to use for initial message */
  initialProvider?: ProviderType;
  /** Quality tier for Claude model routing */
}

// Filmstrip thumbnails are stable preview.png URLs the browser caches, so they look stale after a
// re-sync. Append a per-slide cache-bust token (bumped on slides-updated) to force a reload. Signed
// URLs already change per fetch — appending would break their signature, so leave those alone.
function withThumbCacheBust(url: string | undefined, version: number | undefined): string | undefined {
  if (!url || !version) return url;
  if (/[?&](X-Amz-Signature|X-Goog-Signature|Signature)=/.test(url)) return url;
  return url + (url.includes('?') ? '&' : '?') + 'cb=' + version;
}

export const VideoWorkspaceShell: React.FC<VideoWorkspaceShellProps> = ({
  conversationId,
  mode = 'video',
  initialTab,
  creationMode,
  autoSend = false,
  initialMessage,
  frontDoor,
  initialProvider = 'gemini',
}) => {
  const isMobile = useIsMobile();

  // Workspace CDN session (Phase B): sets the project-scoped CloudFront cookie
  // so the player can fetch baked slides from cdn.4klabs.ai. No-op when the
  // flag is off server-side; the player always has the /serve fallback.
  useWorkspaceCdnSession(conversationId);

  const {
    conversationId: storeConversationId,  // Used to gate rendering until store is synced
    setConversationId,
    slides,  // Get slides reactively for fetchActiveSlide
    setSlides,
    setCurrentSlideId,
    setCurrentSlideData,
    setSlideLoading,
    setVersions,
    setCurrentVersion,
    currentSlideId,
    triggerPreviewRefresh,
    reset,
    // Cache actions
    getCachedSlide,
    setCachedSlide,
    invalidateSlideCache,
    // Outline actions
    outline,
    setOutline,
    setOutlineLoading,
    setSelectedOutlineSlide,
    setTemplateLayouts,
    // Outline generation tracking
    setOutlineGenerating,
    setOutlineGeneratingThinking,
    setOutlineNeedsInput,
    // Edit isolation - for cleanup on unmount (only setters needed, state accessed via getState() in handlers)
    setEditMode,
    setEditingSlide,
    clearPendingSlideUpdate,
  } = useVideoWorkspaceStore();

  // Get conversation store to sync active conversation for ChatArea
  const { setActiveConversation } = useConversationStore();

  // Get current model from settings
  const { currentModel } = useSettingsStore();

  // Message sending hook for autoSend
  const { sendMessage } = useMessage();

  // Track previous conversation for cache cleanup
  const prevConversationIdRef = React.useRef<string | null>(null);

  // Track if autoSend has been triggered (to prevent duplicate sends)
  const autoSendTriggeredRef = useRef(false);

  // Initialize conversation context and clear ALL stale conversation-specific data
  // This is critical to prevent cross-contamination between workspaces
  useEffect(() => {
    // Clear cache for previous conversation when switching
    if (prevConversationIdRef.current && prevConversationIdRef.current !== conversationId) {
      console.log('🧹 WorkspaceShell - Clearing cache for previous conversation:', prevConversationIdRef.current);
      invalidateSlideCache(prevConversationIdRef.current);
      // Also clear narration cache for the previous conversation
      useVideoWorkspaceStore.getState().invalidateNarrationCache(prevConversationIdRef.current);
    }
    prevConversationIdRef.current = conversationId;

    // CRITICAL: Clear ALL previous conversation's data FIRST, before setting conversationId
    // The rendering gate (isStoreReady) waits for storeConversationId === conversationId,
    // so we must clear all state BEFORE setting the conversationId to ensure children
    // don't render with stale data.

    // Slides
    setSlides([]);
    setCurrentSlideId(null);
    setCurrentSlideData(null);
    // Versions
    setVersions([]);
    setCurrentVersion(null);
    // Outline - CRITICAL: must clear to prevent seeing another workspace's outline
    setOutline(null);
    setSelectedOutlineSlide(null);
    setTemplateLayouts([]);
    // Outline generation state
    setOutlineGenerating(false);
    setOutlineGeneratingThinking(undefined);
    setOutlineNeedsInput(false);
    // Clear local edit tracking - new conversation has no pending edits
    useVideoWorkspaceStore.getState().setOutlineLocalEditTimestamp(null);
    // Chat context (slide conversations from video workflow)
    useVideoWorkspaceStore.getState().setSlideConversations([]);
    useVideoWorkspaceStore.getState().setActiveChatContext('outline');
    // Edit isolation state - clear to prevent stuck edit mode from previous conversation
    setEditMode(false);
    setEditingSlide(null, null);
    clearPendingSlideUpdate();

    // LAST: Set the conversationId to signal that store is ready for new conversation
    // This allows the rendering gate to pass and children to render with clean state
    setConversationId(conversationId);
    // Also set in conversation store so ChatArea/MessageInput can access it
    setActiveConversation(conversationId);

    console.log('✅ WorkspaceShell - Store cleared and ready for conversation:', conversationId);

    return () => {
      // Reset non-persisted state when unmounting
      reset();
    };
  }, [conversationId, setConversationId, setActiveConversation, setSlides, setCurrentSlideId, setCurrentSlideData, setVersions, setCurrentVersion, setOutline, setSelectedOutlineSlide, setTemplateLayouts, setOutlineGenerating, setOutlineGeneratingThinking, setOutlineNeedsInput, reset, invalidateSlideCache, setEditMode, setEditingSlide, clearPendingSlideUpdate]);

  // ═══════════════════════════════════════════════════════════════════
  // AUTO-SEND - Trigger initial message from hero page navigation
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Only trigger once per mount when autoSend is true
    if (!autoSend || autoSendTriggeredRef.current || !initialMessage?.trim()) {
      return;
    }

    // Check if outline already exists (prevents StrictMode double-send)
    // Also use sessionStorage as a cross-mount guard for this conversation
    const autoSendKey = `autoSend-${conversationId}`;
    if (sessionStorage.getItem(autoSendKey)) {
      console.log('🔒 VideoWorkspaceShell - AutoSend already triggered for this conversation (sessionStorage guard)');
      return;
    }

    // Mark as triggered at both ref and sessionStorage level
    autoSendTriggeredRef.current = true;
    sessionStorage.setItem(autoSendKey, 'true');

    console.log('🚀 VideoWorkspaceShell - AutoSend triggered:', {
      conversationId,
      messageLength: initialMessage.length,
      provider: initialProvider,
      mode,
    });

    // Set outline generating state to show the generating UI
    setOutlineGenerating(true);

    // Build context for VIDEO conversation (outline creation)
    const context: MessageContext = {
      source: 'outline',
      outlineAction: 'create', // New conversation = create mode
    };

    // Send the initial message with outline context
    const sendInitialMessage = async () => {
      try {
        await sendMessage({
          conversationId,
          content: initialMessage.trim(),
          model: currentModel,
          provider: initialProvider,
          context,
        });

        console.log('✅ VideoWorkspaceShell - Initial message sent successfully');
      } catch (error) {
        console.error('❌ VideoWorkspaceShell - Failed to send initial message:', error);
        toast.error('Failed to start outline generation');
        // Clear generating state on error
        setOutlineGenerating(false);
        // Also clear sessionStorage so user can retry
        sessionStorage.removeItem(autoSendKey);
      }
    };

    // Small delay to ensure conversation is fully initialized
    const timeoutId = setTimeout(sendInitialMessage, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [autoSend, initialMessage, initialProvider, conversationId, currentModel, mode, sendMessage, setOutlineGenerating]);

  // Fetch slides list - memoized for SSE refresh
  const fetchSlides = useCallback(async () => {
    try {
      setSlideLoading(true);
      console.log(`🎬 WorkspaceShell [${mode}] - Fetching slides for:`, conversationId);

      if (mode === 'slide') {
        // SLIDE MODE: Single slide with fixed ID 'slide-001'
        const slideId = 'slide-001';
        const singleSlide = {
          id: slideId,
          title: 'Slide',
          duration: 5,
          path: `slides/${slideId}/slide.json`,
          hasAudio: false,
          thumbnail: undefined,
        };

        console.log('🎬 WorkspaceShell [slide] - Using single slide:', slideId);
        setSlides([singleSlide]);
        setCurrentSlideId(slideId);
      } else {
        // VIDEO MODE: Fetch slides from child conversations (each outline slide = child conversation)
        // First, get the outline to know which slides exist
        const currentOutline = useVideoWorkspaceStore.getState().outline;

        if (currentOutline?.slides) {
          // Build slides from outline, with data from child conversations for completed slides
          // Filter out soft-deleted slides so they don't appear in the filmstrip
          const thumbBust = useVideoWorkspaceStore.getState().slideThumbBust;
          const activeSlides = currentOutline.slides.filter(s => !s.isDeleted);
          const mappedSlides = await Promise.all(
            activeSlides.map(async (outlineSlide, index) => {
              const slideId = outlineSlide.id; // e.g., "slide-001"
              const isCompleted = outlineSlide.generationStatus === 'completed';

              // Base slide info from outline
              const slide = {
                id: slideId,
                title: outlineSlide.title || `Slide ${index + 1}`,
                duration: 5,
                path: undefined as string | undefined,
                hasAudio: false,
                thumbnail: withThumbCacheBust(outlineSlide.generatedThumbnail, thumbBust[slideId]),
                currentVersion: 0,
                // Track child conversation for slide loading
                childConversationId: outlineSlide.slideConversationId,
                status: outlineSlide.generationStatus || 'pending',
              };

              // If completed and has child conversation, construct the path
              if (isCompleted && outlineSlide.slideConversationId) {
                // Slide is in child conversation at slides/slide-001/slide.json
                slide.path = `slides/slide-001/slide.json`;
              }

              return slide;
            })
          );

          console.log('🎬 WorkspaceShell [video] - Mapped slides from outline:', mappedSlides.length);
          setSlides(mappedSlides);

          // Set initial slide if none selected
          const currentState = useVideoWorkspaceStore.getState();
          if (mappedSlides.length > 0 && !currentState.currentSlideId) {
            console.log('🎬 WorkspaceShell [video] - Setting initial slide:', mappedSlides[0].id);
            setCurrentSlideId(mappedSlides[0].id);
          }
        } else {
          // Fallback: Try to fetch slides directly from this conversation (legacy mode)
          const response = await slidesAPI.getByConversation(conversationId);
          const legacyBust = useVideoWorkspaceStore.getState().slideThumbBust;
          const mappedSlides = (response.slides || []).map((s: any, index: number) => {
            const slideId = s.slideIdentifier || `slide-${String(index + 1).padStart(3, '0')}`;
            return {
              id: slideId,
              title: s.title || `Slide ${s.slideNumber || index + 1}`,
              duration: s.metadata?.estimated_duration || 5,
              path: `slides/${slideId}/slide.json`,
              hasAudio: s.hasAudio || false,
              thumbnail: withThumbCacheBust(s.previewUrl || undefined, legacyBust[slideId]),
              currentVersion: s.currentVersion || 0,
            };
          });

          console.log('🎬 WorkspaceShell [video] - Mapped slides (legacy):', mappedSlides.length);
          setSlides(mappedSlides);

          const currentState = useVideoWorkspaceStore.getState();
          if (mappedSlides.length > 0 && !currentState.currentSlideId) {
            setCurrentSlideId(mappedSlides[0].id);
          }
        }
      }
    } catch (error) {
      console.error(`🎬 WorkspaceShell [${mode}] - Failed to load slides:`, error);
      // Only show toast for video mode - slide mode handles empty state gracefully
      if (mode === 'video') {
        toast.error('Failed to load slides for this conversation');
      }
    } finally {
      setSlideLoading(false);
    }
  }, [conversationId, mode, setSlides, setCurrentSlideId, setSlideLoading]);

  // Initial fetch on mount - different logic based on mode
  useEffect(() => {
    if (!conversationId) return;

    if (mode === 'outline' || mode === 'video') {
      // OUTLINE or VIDEO MODE: Fetch outline first (video mode needs outline to know child conversations)
      const fetchOutlineAndSlides = async () => {
        try {
          setOutlineLoading(true);
          console.log(`📋 WorkspaceShell [${mode}] - Fetching outline for:`, conversationId);

          // Fetch outline and layouts in parallel
          const [outlineResponse, layoutsResponse] = await Promise.all([
            outlineAPI.get(conversationId).catch(() => null),
            outlineAPI.getTemplateLayouts(conversationId).catch(() => ({ layouts: [] })),
          ]);

          if (outlineResponse) {
            console.log(`📋 WorkspaceShell [${mode}] - Outline loaded:`, {
              status: outlineResponse.status,
              slideCount: outlineResponse.slides?.length || 0,
            });
            setOutline(outlineResponse);

            // Select first slide if any (outline mode)
            if (mode === 'outline' && outlineResponse.slides?.length > 0) {
              setSelectedOutlineSlide(outlineResponse.slides[0].id);
            }
          } else {
            console.log(`📋 WorkspaceShell [${mode}] - No outline yet`);
            setOutline(null);
          }

          if (layoutsResponse?.layouts) {
            console.log(`📋 WorkspaceShell [${mode}] - Layouts loaded:`, layoutsResponse.layouts.length);
            setTemplateLayouts(layoutsResponse.layouts);
          }

          // For VIDEO mode, slides will be fetched by a separate effect when outline state changes
        } catch (error) {
          console.error(`📋 WorkspaceShell [${mode}] - Failed to load outline:`, error);
          toast.error('Failed to load outline');
        } finally {
          setOutlineLoading(false);
        }
      };

      fetchOutlineAndSlides();
    } else {
      // SLIDE MODE: Fetch slides directly
      fetchSlides();
    }
  }, [conversationId, mode, fetchSlides, setOutline, setOutlineLoading, setSelectedOutlineSlide, setTemplateLayouts]);

  // VIDEO MODE: Map outline → slides whenever the outline (or conversation) actually changes.
  //
  // Previously this effect also depended on `currentSlideId`, which meant every slide
  // transition during playback re-ran the mapping, called setSlides(newArrayRef) with
  // structurally identical data, and cascaded re-renders into every consumer of
  // `slides`. With heavy cinematic decks that storm pushed Chrome into a stuck main
  // thread (INP > 7s) and eventually OOM'd the tab on replay. The "seed initial slide"
  // step was the only reason currentSlideId was in deps — that's now its own effect.
  useEffect(() => {
    if (mode !== 'video' || !outline?.slides) return;

    // CRITICAL: Guard against stale outline data during conversation switches
    // The outline might be from a previous conversation if the fetch hasn't completed yet
    // Check that the store's conversationId matches the prop to ensure we're using fresh data
    if (storeConversationId !== conversationId) {
      console.log('🚫 WorkspaceShell [video] - Skipping stale outline mapping:', {
        storeConversationId,
        propConversationId: conversationId,
      });
      return;
    }

    // Filter out soft-deleted slides so they don't appear in the filmstrip
    const activeOutlineSlides = outline.slides.filter(s => !s.isDeleted);

    console.log('🎬 WorkspaceShell [video] - Outline available, mapping slides from outline:', {
      totalSlides: outline.slides.length,
      activeSlides: activeOutlineSlides.length,
      conversationId,
      slides: activeOutlineSlides.map(s => ({
        id: s.id,
        status: s.generationStatus,
        hasChildConv: !!s.slideConversationId,
      })),
    });

    // Map slides directly from outline (don't call fetchSlides which reads from getState)
    const thumbBust = useVideoWorkspaceStore.getState().slideThumbBust;
    const mappedSlides = activeOutlineSlides.map((outlineSlide, index) => {
      const slideId = outlineSlide.id;
      const isCompleted = outlineSlide.generationStatus === 'completed';

      return {
        id: slideId,
        title: outlineSlide.title || `Slide ${index + 1}`,
        duration: 5,
        path: isCompleted && outlineSlide.slideConversationId ? `slides/slide-001/slide.json` : undefined,
        hasAudio: false,
        thumbnail: withThumbCacheBust(outlineSlide.generatedThumbnail, thumbBust[slideId]),
        currentVersion: 0,
        childConversationId: outlineSlide.slideConversationId,
        status: outlineSlide.generationStatus || 'pending',
      };
    });

    console.log('🎬 WorkspaceShell [video] - Setting slides from outline:', mappedSlides.length);
    setSlides(mappedSlides);
  }, [mode, outline, setSlides, conversationId, storeConversationId]);

  // VIDEO MODE: Seed the initial selected slide once the slide list is first populated.
  // This is split from the mapping effect above so the slide-mapping doesn't re-run on
  // every slide transition (currentSlideId changes constantly during playback).
  useEffect(() => {
    if (mode !== 'video') return;
    if (currentSlideId) return; // already selected
    if (!slides || slides.length === 0) return; // wait for slides
    console.log('🎬 WorkspaceShell [video] - Setting initial slide:', slides[0].id);
    setCurrentSlideId(slides[0].id);
  }, [mode, slides, currentSlideId, setCurrentSlideId]);

  // Fetch active slide data when currentSlideId or slides change
  // Uses smart caching - only fetches if cache miss or version mismatch
  // Skip for outline mode - it doesn't load slides
  // IMPORTANT: Uses reactive `slides` from store, NOT getState().slides, to avoid race conditions
  // IMPORTANT: Skip updates when in edit mode to prevent losing unsaved changes
  useEffect(() => {
    const fetchActiveSlide = async () => {
      if (!currentSlideId || mode === 'outline') return;

      // CRITICAL: Skip slide data updates when in edit mode to prevent losing unsaved changes
      // The edit mode iframe manages its own state - we should not update currentSlideData
      // until the user explicitly exits edit mode (save or cancel)
      const { isEditMode: isEditing, editingSlideId } = useVideoWorkspaceStore.getState();
      if (isEditing && editingSlideId === currentSlideId) {
        console.log('🛡️ VideoWorkspaceShell - Skipping slide fetch: currently editing this slide');
        return;
      }

      // Use reactive slides from store (not getState() which can be stale)
      // This fixes race condition where slide-001 is selected before slides state is updated
      const slideInfo = slides.find(s => s.id === currentSlideId);

      // Wait for slides to be populated before proceeding
      if (slides.length === 0) {
        console.log('🎬 VideoWorkspaceShell - Waiting for slides to be populated');
        return;
      }

      const slideVersion = slideInfo?.currentVersion || 0;

      // For video mode, check if slide has child conversation (generated from outline)
      const isVideoSlide = mode === 'video' && slideInfo?.childConversationId;
      const targetConversationId = isVideoSlide ? slideInfo.childConversationId! : conversationId;
      // In child conversations, slide is always slide-001
      const targetSlideId = isVideoSlide ? 'slide-001' : currentSlideId;

      // Skip if slide is not yet generated or is still generating
      // This handles both initial generation and regeneration (where childConversationId exists but slide isn't ready)
      if (mode === 'video' && slideInfo?.status !== 'completed') {
        console.log('🎬 VideoWorkspaceShell - Slide not yet generated:', currentSlideId, 'status:', slideInfo?.status);
        setCurrentSlideData(null);
        return;
      }

      // Check cache first
      const cached = getCachedSlide(conversationId, currentSlideId);
      if (cached && cached.version === slideVersion) {
        // Cache hit with matching version - use cached data
        console.log('📦 VideoWorkspaceShell - Cache HIT for slide:', {
          slideId: currentSlideId,
          cachedVersion: cached.version,
          currentVersion: slideVersion,
        });
        setCurrentSlideData(cached.data);
        return;
      }

      // Cache miss or version mismatch - fetch from API
      console.log('🔄 VideoWorkspaceShell - Cache MISS, fetching slide:', {
        slideId: currentSlideId,
        targetConversationId,
        targetSlideId,
        childConversationId: slideInfo?.childConversationId,
        cachedVersion: cached?.version,
        currentVersion: slideVersion,
        reason: cached ? 'version mismatch' : 'not in cache',
      });

      try {
        setSlideLoading(true);

        const data = await slidesAPI.getGsapSlide(targetSlideId, targetConversationId);

        console.log('🎬 VideoWorkspaceShell - Slide data received:', {
          id: data.id,
          databaseId: data.databaseId,
          slideIdentifier: data.slideIdentifier,
          currentVersion: data.currentVersion,
          keys: Object.keys(data),
        });

        // ALWAYS override ID to match the requested slide (use outline slide ID for video mode)
        // The API returns JSON content which may have a different id baked in
        data.id = currentSlideId;

        // Also ensure path is set for extractSlideId to work correctly
        if (!data.path) {
          data.path = `slides/${targetSlideId}/slide.json`;
        }

        // Store in cache with version (keyed by parent conversation + outline slide ID)
        setCachedSlide(conversationId, currentSlideId, data, slideVersion);

        useVideoWorkspaceStore.getState().applyLoadedSlide(conversationId, currentSlideId, data);
      } catch (error) {
        console.error('🎬 VideoWorkspaceShell - Failed to load slide content:', error);
        const stillOnScreen = useVideoWorkspaceStore.getState().applyLoadedSlide(conversationId, currentSlideId, null);
        // Don't show toast for video slides that haven't been generated yet
        if (stillOnScreen && !(mode === 'video' && slideInfo?.status !== 'completed')) {
          toast.error('Failed to load slide content');
        }
      } finally {
        setSlideLoading(false);
      }
    };

    fetchActiveSlide();
  }, [currentSlideId, conversationId, mode, slides, getCachedSlide, setCachedSlide, setCurrentSlideData, setSlideLoading]);

  // Fetch versions when currentSlideId changes (skip for outline mode)
  useEffect(() => {
    const fetchVersions = async () => {
      if (!currentSlideId || !conversationId || mode === 'outline') {
        setVersions([]);
        setCurrentVersion(null);
        return;
      }

      // For video mode, use child conversation ID for version fetching
      // CRITICAL: Use reactive `slides` from hook, NOT getState().slides
      // getState() returns stale data during conversation switches
      const slideInfo = slides.find(s => s.id === currentSlideId);
      const isVideoSlide = mode === 'video' && slideInfo?.childConversationId;
      const targetConversationId = isVideoSlide ? slideInfo.childConversationId! : conversationId;
      const targetSlideId = isVideoSlide ? 'slide-001' : currentSlideId;

      // Skip if slide is not generated yet
      if (mode === 'video' && !slideInfo?.childConversationId) {
        setVersions([]);
        setCurrentVersion(null);
        return;
      }

      try {
        console.log('📋 VideoWorkspaceShell - Loading versions for:', currentSlideId, 'from:', targetConversationId);
        const versionData = await slidesAPI.listVersions(targetConversationId, targetSlideId);

        console.log('✅ VideoWorkspaceShell - Versions loaded:', {
          count: versionData.versions?.length || 0,
          currentVersion: versionData.currentVersion,
        });

        useVideoWorkspaceStore.getState().applyLoadedVersions(conversationId, currentSlideId, versionData.versions || [], versionData.currentVersion);
      } catch (error) {
        console.error('❌ VideoWorkspaceShell - Failed to load versions:', error);
        // Silently fail - versioning is optional
        useVideoWorkspaceStore.getState().applyLoadedVersions(conversationId, currentSlideId, [], null);
      }
    };

    fetchVersions();
  }, [currentSlideId, conversationId, mode, slides, setVersions, setCurrentVersion]);

  // ═══════════════════════════════════════════════════════════════════
  // SSE REFRESH - Listen for slide updates from chat/streaming
  // ═══════════════════════════════════════════════════════════════════

  // Reload current slide data - used by SSE refresh
  const reloadCurrentSlide = useCallback(async () => {
    const state = useVideoWorkspaceStore.getState();
    const slideId = state.currentSlideId;
    if (!slideId) return;

    // Find the slide to get childConversationId (for video workflow)
    const slide = state.slides.find(s => s.id === slideId);
    const targetConversationId = slide?.childConversationId || conversationId;
    // For video workflow, slide is always slide-001 in the child conversation
    const targetSlideId = slide?.childConversationId ? 'slide-001' : slideId;

    try {
      console.log(`🔄 WorkspaceShell [${mode}] - Reloading slide:`, slideId, 'from conversation:', targetConversationId);

      const data = await slidesAPI.getGsapSlide(targetSlideId, targetConversationId);
      data.id = slideId; // Keep the original slide ID for the UI
      if (!data.path) {
        data.path = `slides/${targetSlideId}/slide.json`;
      }

      if (!useVideoWorkspaceStore.getState().applyLoadedSlide(conversationId, slideId, data)) return;

      // Reload versions too
      const versionData = await slidesAPI.listVersions(targetConversationId, targetSlideId);
      const stillOnScreen = useVideoWorkspaceStore.getState().applyLoadedVersions(conversationId, slideId, versionData.versions || [], versionData.currentVersion);

      // Update cache with new data and version
      const newVersion = versionData.currentVersion || 0;
      setCachedSlide(conversationId, slideId, data, newVersion);

      // Trigger iframe refresh
      if (stillOnScreen) triggerPreviewRefresh();

      console.log(`✅ WorkspaceShell [${mode}] - Slide reloaded and cached (v${newVersion})`);
    } catch (error) {
      console.error(`❌ WorkspaceShell [${mode}] - Failed to reload slide:`, error);
    }
  }, [conversationId, mode, triggerPreviewRefresh, setCachedSlide]);

  // PRIMARY: Listen for slides-updated event from chat message completion
  // In video mode, slides are in child conversations, so we need to check both:
  // 1. Direct match with parent conversation ID (for slide mode / legacy)
  // 2. Match with any child conversation ID (for video mode with outline)
  useEffect(() => {
    const handleSlideUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { conversationId: updatedConvId, source, outlineSlideId, currentSlideVersions } = customEvent.detail;

      // Check for direct match with parent conversation
      const isDirectMatch = updatedConvId === conversationId;

      // Check for match with any child conversation (video mode)
      // Each slide in the outline has a slideConversationId (child conversation)
      const currentOutline = useVideoWorkspaceStore.getState().outline;
      const currentSlides = useVideoWorkspaceStore.getState().slides;

      // Find if the updated conversation is a child conversation of this workspace
      let matchedOutlineSlideId: string | null = null;

      if (mode === 'video' && !isDirectMatch) {
        // Check outline slides for matching child conversation
        const matchingOutlineSlide = currentOutline?.slides?.find(
          slide => slide.slideConversationId === updatedConvId
        );
        if (matchingOutlineSlide) {
          matchedOutlineSlideId = matchingOutlineSlide.id;
        }

        // Also check slides array (for completed slides with childConversationId)
        if (!matchedOutlineSlideId) {
          const matchingSlide = currentSlides.find(
            slide => slide.childConversationId === updatedConvId
          );
          if (matchingSlide) {
            matchedOutlineSlideId = matchingSlide.id;
          }
        }
      }

      const isChildMatch = matchedOutlineSlideId !== null;
      const shouldProcess = isDirectMatch || isChildMatch;

      console.log(`🔄 WorkspaceShell [${mode}] - Received slides-updated event:`, {
        eventConversationId: updatedConvId,
        myConversationId: conversationId,
        isDirectMatch,
        isChildMatch,
        matchedOutlineSlideId,
        shouldProcess,
        source,
      });

      if (shouldProcess) {
        // Bump the per-slide thumbnail cache-bust so the filmstrip reloads the new preview (the URL
        // is otherwise stable and the browser serves the cached image). Done before the refetch below
        // so the rebuilt slides array carries the new token.
        const bustIds = [
          ...(matchedOutlineSlideId ? [matchedOutlineSlideId] : []),
          ...(outlineSlideId ? [outlineSlideId] : []),
          ...(currentSlideVersions ? Object.keys(currentSlideVersions) : []),
        ];
        if (bustIds.length) useVideoWorkspaceStore.getState().bumpSlideThumbBust(bustIds);

        // Check if we're currently editing a slide
        const { isEditMode, editingConversationId, editingSlideId, setPendingSlideUpdate, currentSlideId } = useVideoWorkspaceStore.getState();

        // Determine if the update is for the slide currently being edited
        // In direct match mode: updatedConvId is this conversation and currentSlideId is being edited
        // In child match mode: matchedOutlineSlideId equals editingSlideId
        const isUpdateForEditingSlide = isEditMode && (
          (isDirectMatch && editingConversationId === conversationId && editingSlideId === currentSlideId) ||
          (isChildMatch && editingSlideId === matchedOutlineSlideId)
        );

        // In video mode, refetch the outline first so fetchSlides() maps from fresh data.
        // sync_slide_to_production updates generationStatus in the DB outline, but the
        // store's outline is stale — without this refresh, new slides appear empty.
        const refreshOutlineThenSlides = async () => {
          try {
            const response = await outlineAPI.get(conversationId);
            if (response) {
              const { outlineLocalEditTimestamp, outline: currentOutline } = useVideoWorkspaceStore.getState();
              if (outlineLocalEditTimestamp && currentOutline) {
                setOutline(mergeServerOutlineIntoLocal(currentOutline, response));
              } else {
                setOutline(response);
              }
            }
          } catch (err) {
            console.error('Failed to refresh outline before fetchSlides:', err);
          }
          fetchSlides();
        };

        if (isUpdateForEditingSlide) {
          // DON'T refresh - queue the update and notify user
          console.log(`⚠️ WorkspaceShell [${mode}] - Update queued: user is editing this slide`);
          setPendingSlideUpdate({
            conversationId: isChildMatch ? (updatedConvId) : conversationId,
            slideId: editingSlideId!,
            timestamp: Date.now()
          });
          // Still fetch slides list for video mode (to update filmstrip)
          if (mode === 'video') {
            refreshOutlineThenSlides();
          }
        } else {
          // Safe to refresh - not editing this slide
          console.log(`🔄 WorkspaceShell [${mode}] - ✅ Match! Invalidating cache and reloading...`);

          // Invalidate ALL slides for this conversation EXCEPT the one being edited
          if (isEditMode && editingConversationId === conversationId && editingSlideId) {
            // Selective invalidation - skip the editing slide
            const allSlides = useVideoWorkspaceStore.getState().slides;
            allSlides.forEach(slide => {
              if (slide.id !== editingSlideId) {
                invalidateSlideCache(conversationId, slide.id);
              }
            });
          } else {
            // No slide being edited, invalidate all
            invalidateSlideCache(conversationId);
          }

          // For video mode, refetch outline then slides to pick up new generationStatus
          if (mode === 'video') {
            refreshOutlineThenSlides();
          }

          // Only reload current slide if it's not being edited
          if (!isEditMode || editingSlideId !== currentSlideId) {
            reloadCurrentSlide();
          }
        }

        // Auto-focus the freshly-synced slide: follow along to whatever just generated/updated so the
        // user always sees the latest slide instead of hunting in the filmstrip. Video mode only. The
        // sync event arrives as a direct match on the parent project carrying `outlineSlideId`, so
        // target that (or the child-match slide). Skip when the user is editing that slide (queued
        // above), just made an explicit filmstrip pick (respect it briefly), or is actively playing a
        // slide / the composition — never yank them off what they're watching; the live content still
        // streams in (cache invalidate + refetch + thumbnail bust above), we just don't move. During
        // bulk generation we DO follow each slide as it completes — that's the desired "watch it build".
        // Not in Studio B: the studio's own follow and the person's pick move its stage.
        const autoNavSlideId = matchedOutlineSlideId || (isDirectMatch ? outlineSlideId : null);
        if (mode === 'video' && autoNavSlideId && !isUpdateForEditingSlide && !studioBOn()) {
          const st = useVideoWorkspaceStore.getState();
          const recentlyManual = Date.now() - (st.lastManualSlideSelectAt || 0) < 4000;
          const isPlaying = st.playerState.isPlaying;
          if (!recentlyManual && !isPlaying && st.currentSlideId !== autoNavSlideId) {
            const target = st.slides.find(s => s.id === autoNavSlideId);
            setCurrentSlideId(autoNavSlideId);
            if (target?.childConversationId) {
              st.setActiveChatContext(target.childConversationId);
              setActiveConversation(target.childConversationId);
            }
          }
        }
      }
    };

    window.addEventListener('slides-updated', handleSlideUpdate);
    console.log(`🔄 WorkspaceShell [${mode}] - Listening for slides-updated events`);

    return () => {
      window.removeEventListener('slides-updated', handleSlideUpdate);
    };
  }, [conversationId, mode, fetchSlides, reloadCurrentSlide, invalidateSlideCache]);

  // Listen for outline-updated events (from MCP tools via Redis → SSE)
  useEffect(() => {
    if (!conversationId) return;

    const handleOutlineUpdate = async (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail.conversationId !== conversationId) return;

      console.log(`📋 WorkspaceShell [${mode}] - Received outline-updated event:`, detail.updateType);
      try {
        setOutlineLoading(true);
        const response = await outlineAPI.get(conversationId);
        if (response) {
          const { outlineLocalEditTimestamp, outline: currentOutline } = useVideoWorkspaceStore.getState();
          if (outlineLocalEditTimestamp && currentOutline) {
            setOutline(mergeServerOutlineIntoLocal(currentOutline, response));
          } else {
            setOutline(response);
          }
        }
        // Also refetch slides in video mode since outline changes may affect slide list
        if (mode === 'video') {
          fetchSlides();
        }
      } catch (err) {
        console.error('Failed to refresh outline after MCP update:', err);
      } finally {
        setOutlineLoading(false);
      }
    };

    window.addEventListener('outline-updated', handleOutlineUpdate);
    return () => window.removeEventListener('outline-updated', handleOutlineUpdate);
  }, [conversationId, mode, setOutline, setOutlineLoading, fetchSlides]);

  // Listen for audio/narration events and refresh the narration cache
  // This ensures OutlineSlideCard shows updated audio status without manual refresh
  const narrationRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preloadNarrationData = useVideoWorkspaceStore(state => state.preloadNarrationData);

  useEffect(() => {
    if (!conversationId) return;

    const scheduleNarrationRefresh = (source: string) => {
      // Debounce: coalesce rapid per-slide events into one refresh
      if (narrationRefreshTimer.current) {
        clearTimeout(narrationRefreshTimer.current);
      }
      narrationRefreshTimer.current = setTimeout(() => {
        console.log(`🔄 WorkspaceShell [${mode}] - Refreshing narration cache (source: ${source})`);
        preloadNarrationData(conversationId);
        narrationRefreshTimer.current = null;
      }, 2000);
    };

    const handleAudioEvent = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const audioEvent = detail?.audioEvent || {};
      // Only refresh on completion/error, not progress ticks
      if (audioEvent.status === 'COMPLETED' || audioEvent.status === 'FAILED' ||
          audioEvent.type === 'audio_generated' || audioEvent.type === 'audio_error') {
        scheduleNarrationRefresh(`audio-event:${audioEvent.type}`);
      }
    };

    const handleNarrationUpdated = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail.conversationId !== conversationId) return;
      scheduleNarrationRefresh(`narration-updated:${detail.updateType}`);
    };

    window.addEventListener('audio-event', handleAudioEvent);
    window.addEventListener('narration-updated', handleNarrationUpdated);

    return () => {
      window.removeEventListener('audio-event', handleAudioEvent);
      window.removeEventListener('narration-updated', handleNarrationUpdated);
      if (narrationRefreshTimer.current) {
        clearTimeout(narrationRefreshTimer.current);
      }
    };
  }, [conversationId, mode, preloadNarrationData]);

  // Listen for project-created events (agent adopted this conversation as a video project)
  useEffect(() => {
    if (!conversationId) return;

    const handleProjectCreated = async (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail.conversationId !== conversationId) return;

      console.log(`🆕 WorkspaceShell [${mode}] - project-created for current conversation`);
      try {
        setOutlineLoading(true);
        const response = await outlineAPI.get(conversationId);
        if (response) {
          setOutline(response);
        }
        if (mode === 'video') {
          fetchSlides();
        }
      } catch (err) {
        console.error('Failed to refresh after project-created:', err);
      } finally {
        setOutlineLoading(false);
      }
    };

    window.addEventListener('project-created', handleProjectCreated);
    return () => window.removeEventListener('project-created', handleProjectCreated);
  }, [conversationId, mode, setOutline, setOutlineLoading, fetchSlides]);

  // NOTE: manifest-updated is handled in VideoWorkspacePanels where setVideoManifest lives.
  // It re-fetches the manifest from the API and updates the composition player.

  // FALLBACK: SSE subscription for edge cases (slides updated outside chat)
  useEffect(() => {
    const unsubscribe = conversationUpdatesService.onConversationUpdate(
      (updateConversationId: string, updates: {
        slidesUpdated?: boolean;
        outlineUpdated?: boolean;
        outlineStatus?: string;
        outlineSlideThinking?: boolean;
        outlineSlideCompleted?: boolean;
        slideId?: string;
        childConversationId?: string;
        thinking?: string;
        updateType?: string;
        outlineSlideId?: string;
      }) => {
        // Debug: Log all received events to diagnose routing issues
        console.log(`🔔 WorkspaceShell [${mode}] - SSE update received:`, {
          updateConversationId: updateConversationId?.slice(0, 8),
          myConversationId: conversationId?.slice(0, 8),
          match: updateConversationId === conversationId,
          updates: Object.keys(updates).filter(k => updates[k as keyof typeof updates]),
        });

        if (updateConversationId !== conversationId) return;

        // Handle slide generation thinking updates
        if (updates.outlineSlideThinking && updates.slideId) {
          console.log(`💭 WorkspaceShell - Slide ${updates.slideId} thinking:`, updates.thinking?.substring(0, 50));
          // Update or add the slide conversation's thinking in the store
          const { updateSlideConversation, addSlideConversation, slideConversations, outline, conversationId: storeConvId } = useVideoWorkspaceStore.getState();
          const slideConv = slideConversations.find(sc => sc.outlineSlideId === updates.slideId);

          console.log(`💭 WorkspaceShell - slideConversations count: ${slideConversations.length}, found slideConv: ${!!slideConv}, storeConvId: ${storeConvId?.slice(0,8)}`);
          console.log(`💭 WorkspaceShell - slideConversations:`, slideConversations.map(sc => `${sc.outlineSlideId}:${sc.status}`).join(', '));

          if (slideConv) {
            // Always update thinking when we receive a thinking event - this means
            // the slide is actively generating (either initial generation or user chat)
            console.log(`💭 WorkspaceShell - Updating existing slideConv for ${updates.slideId}, current status: ${slideConv.status}`);
            updateSlideConversation(updates.slideId, {
              generatingThinking: updates.thinking,
              status: 'generating',
            });
          } else if (updates.childConversationId) {
            // SSE arrived before we had the slideConversation - add it now
            const outlineSlide = outline?.slides.find(s => s.id === updates.slideId);
            const childConvId = updates.childConversationId as string;
            console.log(`💭 WorkspaceShell - Adding new slideConversation for ${updates.slideId}, childConvId: ${childConvId?.slice(0, 8)}`);
            addSlideConversation({
              id: childConvId,
              outlineSlideId: updates.slideId,
              title: outlineSlide?.title || `Slide ${updates.slideId}`,
              status: 'generating',
              generatingThinking: updates.thinking,
            });

            // If this is the currently selected slide, update activeConversation for ChatArea
            const { currentSlideId } = useVideoWorkspaceStore.getState();
            if (updates.slideId === currentSlideId) {
              console.log(`💭 WorkspaceShell - Setting active conversation to child: ${childConvId?.slice(0, 8)} for current slide`);
              setActiveConversation(childConvId);
            }
          } else {
            console.log(`💭 WorkspaceShell - No slideConv found and no childConversationId - cannot update thinking`);
          }
        }

        // Handle slide generation completed
        if (updates.outlineSlideCompleted && updates.slideId) {
          console.log(`✅ WorkspaceShell - Slide ${updates.slideId} completed`);

          // Update slideConversation status immediately to clear thinking in filmstrip
          const { updateSlideConversation, slideConversations } = useVideoWorkspaceStore.getState();
          const slideConv = slideConversations.find(sc => sc.outlineSlideId === updates.slideId);

          if (slideConv) {
            updateSlideConversation(updates.slideId, {
              status: 'completed',
              generatingThinking: undefined, // Clear thinking
            });
            console.log(`✅ WorkspaceShell - Updated slideConversation status to completed for ${updates.slideId}`);
          }

          // Refetch outline to get full updated status, using merge if user has pending edits
          outlineAPI.get(conversationId).then((outlineResponse) => {
            if (outlineResponse) {
              const { outlineLocalEditTimestamp, outline: currentOutline } = useVideoWorkspaceStore.getState();
              if (outlineLocalEditTimestamp && currentOutline) {
                setOutline(mergeServerOutlineIntoLocal(currentOutline, outlineResponse));
              } else {
                setOutline(outlineResponse);
              }
            }
          }).catch(err => console.error('Failed to refresh outline:', err));
        }

        // Handle slide updates
        if (updates.slidesUpdated) {
          const outlineSlideId = updates.outlineSlideId || updates.slideId;

          // Get edit state to check if we should protect the editing slide
          const { isEditMode: isEditing, editingSlideId: editSlideId, currentSlideId: currSlideId, setPendingSlideUpdate: setPending } = useVideoWorkspaceStore.getState();

          console.log(`🔄 WorkspaceShell [${mode}] - SSE slide update:`, {
            updateType: updates.updateType,
            outlineSlideId,
            childConversationId: updates.childConversationId,
            currentSlideId: currSlideId,
            isEditMode: isEditing,
            editingSlideId: editSlideId,
          });

          // Check if this update is for the slide currently being edited
          const isUpdateForEditingSlide = isEditing && editSlideId && (
            // Direct match: editing slide is the one being updated
            editSlideId === outlineSlideId ||
            // Or if no specific slide ID, any update while editing current slide
            (!outlineSlideId && editSlideId === currSlideId)
          );

          if (isUpdateForEditingSlide) {
            // DON'T refresh - queue the update and notify user
            console.log(`⚠️ WorkspaceShell [${mode}] - SSE update queued: user is editing this slide`);
            setPending({
              conversationId: updates.childConversationId || conversationId,
              slideId: editSlideId!,
              timestamp: Date.now()
            });

            // Still fetch slides list for video mode (to update filmstrip for other slides)
            if (mode === 'video') {
              fetchSlides();
            }
          } else {
            // Safe to refresh - not editing this specific slide
            // Use selective invalidation if in edit mode
            if (isEditing && editSlideId) {
              const allSlides = useVideoWorkspaceStore.getState().slides;
              allSlides.forEach(slide => {
                if (slide.id !== editSlideId) {
                  invalidateSlideCache(conversationId, slide.id);
                }
              });
            } else {
              invalidateSlideCache(conversationId);
            }

            if (mode === 'video') {
              fetchSlides();

              // If this update is for the currently selected slide, trigger preview refresh too
              if (outlineSlideId && outlineSlideId === currSlideId && !isEditing) {
                console.log(`🔄 WorkspaceShell [${mode}] - Update matches current slide, triggering preview refresh`);
                triggerPreviewRefresh();
              }
            }

            // Only reload current slide if not editing
            if (!isEditing || editSlideId !== currSlideId) {
              reloadCurrentSlide();
            }
          }
        }

        // Handle outline updates (for outline mode)
        if (updates.outlineUpdated) {
          console.log(`📋 WorkspaceShell [${mode}] - SSE outline update, status:`, updates.outlineStatus);

          // Invalidate narration cache since outline changed (slides may have been added/removed/reordered)
          useVideoWorkspaceStore.getState().invalidateNarrationCache(conversationId);

          // Refetch outline data
          const refetchOutline = async () => {
            try {
              setOutlineLoading(true);
              const outlineResponse = await outlineAPI.get(conversationId);

              if (outlineResponse) {
                console.log('📋 WorkspaceShell - Outline refreshed:', {
                  status: outlineResponse.status,
                  slideCount: outlineResponse.slides?.length || 0,
                });

                // Use merge if user has pending local edits to avoid overwriting
                const { outlineLocalEditTimestamp, outline: currentOutline } = useVideoWorkspaceStore.getState();
                if (outlineLocalEditTimestamp && currentOutline) {
                  setOutline(mergeServerOutlineIntoLocal(currentOutline, outlineResponse));
                } else {
                  setOutline(outlineResponse);
                }

                // Clear generating state - outline is now available
                setOutlineGenerating(false);
                setOutlineGeneratingThinking(undefined);
                setOutlineNeedsInput(false);

                // Select first slide if none selected
                const currentState = useVideoWorkspaceStore.getState();
                if (outlineResponse.slides?.length > 0 && !currentState.selectedOutlineSlideId) {
                  setSelectedOutlineSlide(outlineResponse.slides[0].id);
                }
              }
            } catch (error) {
              console.error('📋 WorkspaceShell - Failed to refresh outline:', error);
            } finally {
              setOutlineLoading(false);
            }
          };

          refetchOutline();
        }
      }
    );

    return () => unsubscribe();
  }, [conversationId, mode, fetchSlides, reloadCurrentSlide, invalidateSlideCache, setOutline, setOutlineLoading, setSelectedOutlineSlide, setOutlineGenerating, setOutlineGeneratingThinking, setOutlineNeedsInput, triggerPreviewRefresh]);

  // ═══════════════════════════════════════════════════════════════════
  // MESSAGE-COMPLETED FALLBACK - Handle completion for child AND parent conversations
  // ═══════════════════════════════════════════════════════════════════
  // This provides a fallback for when outlineSlideCompleted events aren't received
  // by listening to the message-completed CustomEvent which fires for ALL conversations.
  // Also detects when the parent conversation completes without producing an outline
  // (e.g., AI responds with "I can't read .docx files" instead of generating an outline).
  useEffect(() => {
    if (mode !== 'video') return;

    const handleMessageCompleted = (event: Event) => {
      const customEvent = event as CustomEvent<{
        conversationId: string;
        messageId?: string;
        timestamp?: string;
      }>;
      const { conversationId: completedConvId } = customEvent.detail;

      console.log(`📬 WorkspaceShell [${mode}] - message-completed event:`, {
        completedConvId: completedConvId?.slice(0, 8),
        myConversationId: conversationId?.slice(0, 8),
      });

      // Check if this completed conversation is one of our child slide conversations
      const { slideConversations, updateSlideConversation } = useVideoWorkspaceStore.getState();
      const slideConv = slideConversations.find(sc => sc.id === completedConvId);

      if (slideConv) {
        console.log(`✅ WorkspaceShell [${mode}] - Message completed for child slide ${slideConv.outlineSlideId}, updating status`);
        updateSlideConversation(slideConv.outlineSlideId, {
          status: 'completed',
          generatingThinking: undefined,
        });

        // Also refetch outline to get full updated status, using merge if user has pending edits
        outlineAPI.get(conversationId).then((outlineResponse) => {
          if (outlineResponse) {
            const { outlineLocalEditTimestamp, outline: currentOutline } = useVideoWorkspaceStore.getState();
            if (outlineLocalEditTimestamp && currentOutline) {
              setOutline(mergeServerOutlineIntoLocal(currentOutline, outlineResponse));
            } else {
              setOutline(outlineResponse);
            }
          }
        }).catch(err => console.error('Failed to refresh outline after message completed:', err));

        // Refetch slides to update thumbnails
        fetchSlides();
      }

      // Check if this is the PARENT conversation completing without an outline
      // This handles the case where AI responds with text instead of generating an outline
      if (completedConvId === conversationId) {
        const { isOutlineGenerating, outline: currentOutline } = useVideoWorkspaceStore.getState();
        if (isOutlineGenerating && !currentOutline) {
          console.log(`⚠️ WorkspaceShell [${mode}] - Parent conversation completed without outline, showing needs-input state`);
          setOutlineGenerating(false);
          setOutlineGeneratingThinking(undefined);
          setOutlineNeedsInput(true);
        }
      }
    };

    window.addEventListener('message-completed', handleMessageCompleted);
    console.log(`📬 WorkspaceShell [${mode}] - Listening for message-completed events`);

    return () => {
      window.removeEventListener('message-completed', handleMessageCompleted);
    };
  }, [conversationId, mode, setOutline, fetchSlides, setOutlineGenerating, setOutlineGeneratingThinking, setOutlineNeedsInput]);

  // ═══════════════════════════════════════════════════════════════════
  // THINKING SUBSCRIPTION - Pipe message thinking to outline generation UI
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Subscribe to message store changes to get thinking updates
    const unsubscribe = useMessageStore.subscribe((state) => {
      const streamingMessageId = state.streamingMessageId;
      if (!streamingMessageId) return;

      // Get the streaming message
      const message = state.getMessageById(streamingMessageId);
      if (!message) return;

      // Only track thinking for this conversation
      if (message.conversationId !== conversationId) return;

      // Check if we're in outline generating mode
      const workspaceState = useVideoWorkspaceStore.getState();
      if (!workspaceState.isOutlineGenerating) return;

      // Update thinking subject
      if (message.currentThinkingSubject) {
        setOutlineGeneratingThinking(message.currentThinkingSubject);
      }
    });

    return () => unsubscribe();
  }, [conversationId, setOutlineGeneratingThinking]);

  // ═══════════════════════════════════════════════════════════════════
  // REACTIVE RESET - Clear outlineNeedsInput when generation restarts
  // ═══════════════════════════════════════════════════════════════════
  // This catches ALL paths that set isOutlineGenerating=true (autoSend, manual send from chat, etc.)
  const isOutlineGenerating = useVideoWorkspaceStore((s) => s.isOutlineGenerating);
  useEffect(() => {
    if (isOutlineGenerating) {
      setOutlineNeedsInput(false);
    }
  }, [isOutlineGenerating, setOutlineNeedsInput]);

  // CRITICAL: Gate rendering until store conversationId matches prop
  // This prevents cross-conversation data contamination by ensuring children
  // don't render with stale store data before the cleanup useEffect runs.
  // The useEffect sets storeConversationId = conversationId AFTER clearing all state.
  const isStoreReady = storeConversationId === conversationId;

  return (
    <div className={cn(
      "h-full w-full overflow-hidden",
      "workspace-canvas", // Unified gradient canvas background for floating panels
      "text-foreground",
      "transition-colors duration-300"
    )}>
      {/* Three-Panel Layout - Floating glass panels with gaps */}
      {/* RENDERING GATE: Only render children after store is synced to prevent
          cross-conversation contamination. The store is cleared and conversationId
          is set in useEffect, so we wait until that's complete before rendering. */}
      {isStoreReady ? (
        isMobile && !studioBOn()
          ? <MobileVideoWorkspace key={conversationId} conversationId={conversationId} mode={mode} initialMessage={autoSend ? initialMessage : undefined} />
          : <VideoWorkspacePanels key={conversationId} conversationId={conversationId} mode={mode} initialTab={initialTab} creationMode={creationMode} initialMessage={autoSend ? initialMessage : undefined} frontDoor={autoSend ? frontDoor : undefined} />
      ) : (
        <div className="h-full w-full flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            <span className="text-sm text-muted-foreground">Loading workspace...</span>
          </div>
        </div>
      )}
    </div>
  );
};
